// EN — the SOURCE catalogue.
//
// Every key the app renders is declared HERE first, and fa.js, fr.js and de.js
// translate it. A key missing from en.js is a defect and tests/i18n.test.js
// fails on it.
//
// Keys are fully qualified and the first segment is the namespace, so a key you
// read in a screen is a key you can grep for. There is deliberately no scoped
// `useTranslation('today')` form — one rule, one lookup.
//
// Persian is the PRODUCT language; English is the source of record for the
// catalogue. All four catalogues (en, fa, fr, de) are complete and shipped; a
// key missing from fr.js or de.js would fall back to English, never to Persian.

export const en = {
  // ── app ──────────────────────────────────────────────────────────────────
  'app.title': 'Parsina — Dental Module',

  // ── nav / chrome ─────────────────────────────────────────────────────────
  'nav.brand.name': 'Parsina',
  'nav.brand.panel': 'Dentistry',
  'nav.items.today': 'Today',
  'nav.items.patients': 'Patients',
  'nav.items.credits': 'Credit',
  'nav.items.profile': 'Profile',
  'nav.sidebar.collapse': 'Collapse menu',
  'nav.sidebar.expand': 'Expand menu',
  'nav.sidebar.logout': 'Sign out',
  'nav.sidebar.editProfile': 'Profile',
  'nav.clinic.label': 'Clinic',
  // The AccountCard at the foot of the rail — the balance, and the menu that
  // holds every account-level destination. The unit is "Parseh" in BOTH locales.
  'nav.account.menuLabel': 'Account',
  'nav.account.parsina': 'Parsina account',
  'nav.account.parsinaHint': 'Name, password, language',
  'nav.credits.label': 'Your Parseh credit',
  'nav.credits.topUp': 'Top up Parseh',
  'nav.clinicSwitch.label': 'Go to clinic panel',
  // Landmark names. Two <nav>-less/<main>-less pages are indistinguishable to a
  // screen reader's landmark list; the rail and the page body are named so
  // "skip to content" works without a skip link.
  'nav.landmark.primary': 'Main menu',
  'nav.landmark.content': 'Content',

  // ── common ───────────────────────────────────────────────────────────────
  'common.actions.cancel': 'Cancel',
  'common.actions.retry': 'Try again',
  // The accessible name of every skeleton composite (DESIGN.md §"Loading & skeleton").
  'common.loading': 'Loading',
  'common.actions.save': 'Save',
  'common.actions.saving': 'Saving…',
  'common.actions.back': 'Back',
  // Distinct from «انصراف»: a read-only drawer is dismissed, not cancelled —
  // there is nothing pending to abandon.
  'common.actions.close': 'Close',
  'common.actions.clearFilters': 'Clear filters',
  'common.actions.join': 'Join',
  // A button mid-flight. Not «Saving…»: the session dialog's primary
  // action is a token refresh, and nothing is being saved.
  'common.state.pleaseWait': 'Please wait…',
  'common.labels.phone': 'Phone',
  'common.labels.country': 'Country',
  'common.labels.nationalId': 'National ID',
  'common.labels.fileNumber': 'File no.',
  'common.labels.none': '—',
  'common.gender.male': 'Male',
  'common.gender.female': 'Female',
  'common.marital.single': 'Single',
  'common.marital.married': 'Married',
  'common.marital.divorced': 'Divorced',
  'common.marital.widowed': 'Widowed',
  'common.units.years': '{{count}} yrs',
  // The plural `one` form (Intl.PluralRules of the panel language), picked by
  // the translator when `count` is a NUMBER: «1 yr» here, «0 an» / «1 an» in
  // French, where 0 is `one` too.
  'common.units.years_one': '{{count}} yr',
  'common.units.minute': 'min',
  // Spelled out, not 'sec': it sits beside a countdown that a dentist has
  // two minutes to act on, and an abbreviation reads as a table cell.
  'common.units.second': 'seconds',
  // The unit word of CLINIC money in an Iranian clinic (IRR, shown as toman).
  // Not the credits screens' own `credits.toman`: that is platform money and
  // never follows a clinic's currency.
  'common.units.toman': 'toman',
  // The same word AFTER an amount of 2 toman or more (src/lib/money.js
  // moneyUnit). English keeps «toman» invariable, as it always read; French
  // says «tomans».
  'common.units.tomans': 'toman',
  // The colon a screen puts between a label and its value («File no.: 12»).
  // A catalogue entry, not a literal, because French writes a no-break space
  // before it («N° de dossier : 12»).
  'common.punct.colon': ':',
  // Voice dictation (browser SpeechRecognition — see useSpeechToText.js).
  'common.voice.start': 'Dictate',
  'common.voice.stop': 'Stop recording',
  // No ellipsis: the elapsed clock follows it immediately, and «Listening… 00:12»
  // reads as a pause where there is none.
  'common.voice.listening': 'Listening',
  'common.voice.priceTitle': '5 Parseh per minute of recording, rounded up.',
  'common.voice.inlineTranscribing': 'Writing it out…',
  'common.voice.error.denied': 'Microphone access was denied',
  'common.voice.error.no-mic': 'No microphone was found',
  'common.voice.error.unsupported': 'This browser cannot record audio',
  // getUserMedia only exists over HTTPS — what you hit opening the app on an
  // iPad by its LAN IP address.
  'common.voice.error.insecure': 'Recording only works over a secure (HTTPS) connection',
  'common.voice.error.credits': 'Your Parsina credit has run out',
  'common.voice.error.auth': 'Your session is no longer valid. Please sign in again',
  'common.voice.error.onboarding': 'Finish your registration first',
  'common.voice.error.unavailable': 'The speech-to-text service is unavailable',
  'common.voice.error.timeout': 'Writing the text out took too long',
  'common.voice.error.generic': 'Could not record audio',

  // ── mobile shell ─────────────────────────────────────────────────────────
  // Copy that belongs to the ≤768px surface only. The kit's own defaults for
  // these slots (MobileHeader.greeting, BottomSheet.closeLabel) are Persian
  // literals, so every one of them has to be passed explicitly or it leaks
  // Persian into the English UI.
  'mobile.greeting': 'Welcome,',
  // The expander that grows a list. Never infinite scroll: position and
  // citability matter, and a phone list is where losing your place hurts most.
  'mobile.showMore': 'Show more',
  'mobile.close': 'Close',

  // ── auth / boot ──────────────────────────────────────────────────────────
  'auth.checking': 'Signing you in…',
  'auth.loading': 'Loading your workspace…',
  'auth.loopError': 'Sign-in keeps redirecting. Clear your session and try again, or contact support.',
  'auth.loopTitle': 'Sign-in failed',
  'auth.checkingHint': 'This takes a moment',
  'auth.stuck': 'Not redirected?',
  'auth.manualSignIn': 'Click here',
  'auth.retry': 'Try again',
  'auth.signOut': 'Sign out',
  'auth.bootFailed.title': 'Could not start',
  'auth.bootFailed.body': 'Your workspace could not be loaded. This is usually temporary.',
  // The session ending — see src/lib/session.js. Deliberately word-for-word the
  // doctor panel's copy (src/i18n/translations/en/auth.json there): a dentist
  // who also works in the doctor panel meets this dialog in both, and two
  // wordings for one event would read as two different products.
  'auth.session.expiringTitle': 'Your session is about to end',
  'auth.session.expiringBody': 'Extend it to keep working without interruption.',
  'auth.session.staySignedIn': 'Keep working',
  'auth.session.expiredTitle': 'Your session has ended',
  'auth.session.expiredBody': 'Sign in again to continue. This page has not been closed — you will come back to it.',
  'auth.session.signInAgain': 'Sign in again',
  'auth.session.returnNotice': 'Anything you already saved is safe. Note down anything unsaved on screen before signing in again.',

  // ── errors ───────────────────────────────────────────────────────────────
  'errors.generic': 'Something went wrong.',
  'errors.load.today': 'The day could not be loaded.',
  'errors.load.patients': 'The patient list could not be loaded.',
  'errors.load.patient': 'This record could not be loaded.',
  'errors.load.profile': 'Your profile could not be loaded.',
  'errors.load.drugs': 'The formulary could not be searched.',
  'errors.credits.title': 'Not enough Parsina credits',
  'errors.credits.body': 'The interaction check needs credits. Top up from اعتبار — prescribing itself is not blocked.',

  // ── today (امروز) ─────────────────────────────────────────────────────────
  'today.title': 'Today',
  'today.date.today': 'Today',
  'today.date.prev': 'Previous day',
  'today.date.next': 'Next day',
  // Mobile: tapping the day label opens the calendar in a sheet, so any date is
  // one tap away instead of N arrow presses.
  'today.date.pick': 'Pick a date',
  'today.counts.total': 'Appointments',
  'today.counts.here': 'In the clinic',
  'today.counts.scheduled': 'Booked',
  'today.counts.completed': 'Seen',
  'today.counts.cancelled': 'Cancelled',
  // Mobile keeps the stat row at exactly four tiles (the design's signature), so
  // the desktop's conditional fifth KPI becomes this muted line beneath it.
  'today.counts.cancelledLine': '{{count}} cancelled',
  // The waiting room is a fact about NOW, so it exists only for today. Said out
  // loud rather than showing an empty queue block on another day.
  'today.notice.liveQueue': 'The live waiting room is only shown for today. Other days show the booked list.',
  // The appointment lifecycle, in the SCHEDULING service's own vocabulary.
  // The kit's StatusBadge derives its label from `statusColors[status].label`,
  // which is Persian-only — so the day sheet read «نوبت‌دار» inside an otherwise
  // English UI. The colours still come from the kit; only the word is here.
  // An unrecognised status renders verbatim (AppointmentRow), never relabelled.
  'today.status.scheduled': 'Booked',
  'today.status.arrived': 'Arrived',
  'today.status.in_visit': 'In the chair',
  'today.status.completed': 'Seen',
  'today.status.cancelled': 'Cancelled',
  'today.status.no_show': 'No-show',
  'today.blocks.queue': 'Waiting room',
  'today.blocks.upcoming': 'Ahead',
  'today.blocks.done': 'Finished',
  'today.next.title': 'Next patient',
  'today.next.at': 'at {{time}}',
  'today.next.service': 'Service: {{name}}',
  'today.next.serviceVisit': 'Visit',
  'today.next.serviceMore': '+{{count}} more',
  'today.empty.title': 'Nothing booked for this day',
  'today.empty.description': 'Appointments are booked by the clinic front desk and appear here automatically.',
  'today.error.title': 'The day sheet is unavailable',
  'today.queue.positionNext': 'Next',
  'today.queue.position': 'No. {{position}}',
  'today.queue.waited': 'waited {{count}} min',
  'today.queue.waitedHours': 'waited {{hours}} h',
  'today.queue.waitedHoursMinutes': 'waited {{hours}} h {{minutes}} min',
  'today.queue.elapsed': '{{count}} min in the chair',
  'today.queue.elapsedHours': '{{hours}} h in the chair',
  'today.queue.elapsedHoursMinutes': '{{hours}} h {{minutes}} min in the chair',
  'today.row.visit': 'Visit',
  'today.row.visitFor': 'Open the visit for {{name}}',
  'today.row.open': 'Open record',
  // Every row's button reads «Open record»; the accessible name says WHOSE, so
  // a screen reader running the day sheet is not a list of identical buttons.
  'today.row.openFor': 'Open the record of {{name}}',
  'today.row.walkIn': 'Walk-in',
  'today.row.unknownPatient': 'Unnamed patient',
  'today.readOnlyNote': 'Booking, cancelling and queue order belong to the clinic front desk. This panel reads the same appointments.',

  // ── patients (بیماران) ────────────────────────────────────────────────────
  'patients.title': 'Patients',
  'patients.subtitle': 'The clinic register — the same patients the front desk enrols',
  'patients.search.placeholder': 'Name, phone, national ID or file number',
  'patients.search.hint': 'Type at least two characters to search.',
  'patients.count': '{{count}} patients',
  'patients.columns.name': 'Patient',
  'patients.columns.phone': 'Phone',
  'patients.columns.fileNumber': 'File no.',
  'patients.columns.nationalId': 'National ID',
  'patients.empty.title': 'No patients yet',
  'patients.empty.description': 'Patients registered at the clinic front desk appear here.',
  'patients.emptyFiltered.title': 'No match',
  'patients.emptyFiltered.description': 'No patient matches this search.',
  'patients.noClinic.title': 'You are not a member of a clinic yet',
  'patients.noClinic.description': 'The patient register belongs to a clinic. Join one with its invitation code from your profile.',
  'patients.noClinic.action': 'Go to profile',
  'patients.pendingClinic.title': 'Your membership has not been accepted yet',
  'patients.pendingClinic.description':
    'You have asked to join, and the clinic has to accept you before its patient register opens. The status is on your profile.',
  'patients.error.title': 'The patient list is unavailable',

  // ── patient workspace ────────────────────────────────────────────────────
  'patient.tabs.summary': 'Summary',
  'patient.tabs.history': 'History',
  // The mobile record's sticky section strip is a landmark; the kit takes its
  // name as a prop rather than hardcoding a Persian one.
  'patient.tabs.sectionsLabel': 'Record sections',
  'patient.actions.prescribe': 'Prescribe',
  'patient.notFound.title': 'Record not found',
  'patient.notFound.description': 'This patient does not exist, or is not visible to you.',
  'patient.error.title': 'This record is unavailable',
  'patient.stats.age': 'Age',
  'patient.stats.allergies': 'Allergies',
  'patient.stats.medications': 'Medications',
  'patient.summary.demographics': 'Details',
  'patient.summary.allergies': 'Allergies',
  'patient.summary.noAllergies': 'No allergy recorded.',
  // Recorded VALUES, recognised and labelled. An unrecognised value renders
  // verbatim (lib/patients.js) — a clinical field is never relabelled.
  'patient.severity.mild': 'Mild',
  'patient.severity.moderate': 'Moderate',
  'patient.severity.severe': 'Severe',
  'patient.conditionStatus.active': 'Active',
  'patient.conditionStatus.inactive': 'Inactive',
  'patient.conditionStatus.resolved': 'Resolved',
  'patient.conditionStatus.remission': 'In remission',
  'patient.photo.open': 'View a larger photo of {{name}}',
  'patient.photo.alt': 'Profile photo of {{name}}',
  'patient.summary.allergyWarning': 'Recorded allergies — check them before prescribing.',
  'patient.fields.birthDate': 'Date of birth',
  'patient.fields.gender': 'Gender',
  'patient.fields.maritalStatus': 'Marital status',
  'patient.fields.bloodType': 'Blood type',
  'patient.fields.job': 'Occupation',
  'patient.fields.insurance': 'Insurance',
  'patient.fields.height': 'Height',
  'patient.fields.createdAt': 'Registered',
  'patient.history.conditions': 'Conditions',
  'patient.history.medications': 'Medications',
  'patient.history.noConditions': 'No condition recorded.',
  'patient.history.noMedications': 'No medication recorded.',
  'patient.history.prescribedOn': 'Prescribed {{date}}',

  // ── prescription (نسخه) ──────────────────────────────────────────────────
  'prescription.title': 'Prescription',
  'prescription.subtitle': 'For {{name}}',
  'prescription.search.label': 'Add a medicine',
  'prescription.search.placeholder': 'Search the formulary…',
  'prescription.search.searching': 'Searching…',
  'prescription.search.empty': 'No medicine matches.',
  'prescription.search.hint': 'Type at least two characters.',
  // Announced, not drawn: the formulary popup's result count reaches a screen
  // reader through the combobox's live region.
  'prescription.search.results': '{{count}} medicines found.',
  'prescription.item.dose': 'Dose',
  'prescription.item.frequency': 'Frequency',
  'prescription.item.duration': 'Duration',
  'prescription.item.note': 'Note',
  'prescription.item.remove': 'Remove {{name}}',
  'prescription.empty.title': 'Nothing prescribed yet',
  'prescription.empty.description': 'Search the formulary above to add the first medicine.',
  'prescription.save': 'Save prescription',
  'prescription.saveConfirm': 'Confirm and save',
  'prescription.saving': 'Saving…',
  'prescription.saved': 'Prescription saved.',
  'prescription.saveFailed': 'The prescription could not be saved.',
  'prescription.savePartial': '{{saved}} of {{total}} medicines were saved.',
  'prescription.allergies.title': 'Recorded allergies',
  'prescription.interactions.title': 'Interaction check',
  'prescription.interactions.checking': 'Checking…',
  'prescription.interactions.none': 'No interaction found for these medicines.',
  'prescription.interactions.needsTwo': 'Add a second medicine to run the interaction check.',
  'prescription.interactions.unavailable': 'The interaction check is unavailable right now. Prescribing is not blocked.',
  'prescription.interactions.severity.high': 'High',
  'prescription.interactions.severity.moderate': 'Moderate',
  'prescription.interactions.severity.low': 'Low',
  'prescription.interactions.type.interaction': 'Interaction',
  'prescription.interactions.type.contraindication': 'Contraindication',
  'prescription.interactions.type.allergy': 'Allergy',
  'prescription.interactions.type.black_box': 'Black box',
  'prescription.history.title': 'Previously prescribed',
  'prescription.history.empty': 'Nothing prescribed before.',

  // ── profile ──────────────────────────────────────────────────────────────
  'profile.title': 'Profile',
  'profile.subtitle': 'Your identity, your clinics and your shifts',
  'profile.sections.label': 'Profile sections',
  'profile.sections.identity': 'Identity',
  'profile.sections.clinics': 'Clinics',
  'profile.sections.presence': 'Shifts',
  'profile.sections.appearance': 'Appearance & language',
  'profile.fields.fullName': 'Full name',
  'profile.fields.medicalCode': 'Medical council number',
  'profile.fields.specialty': 'Specialty',
  'profile.fields.bio': 'About you',
  // The READABLE label for the dental specialty. The value actually stored and
  // sent is the Persian string in constants/specialties.js — never this. They
  // are separate on purpose: the stored value is a server identifier the
  // normalized predicate matches on, this is copy.
  'profile.specialtyValue': 'Dentistry',
  'profile.medicalCodeLocked': 'The council number was recorded at sign-up and cannot be edited. Contact Parsina support to have it corrected.',
  'profile.specialtyLocked': 'This panel serves dentists, so the specialty is fixed. Changing it elsewhere closes this panel to your account.',
  'profile.saved': 'Profile saved.',
  'profile.noChanges': 'Nothing changed.',
  'profile.saveFailed': 'The profile could not be saved.',
  'profile.duplicateCode': 'That medical council number already belongs to another account.',
  'profile.clinics.empty': 'You are not a member of any clinic yet.',
  'profile.clinics.status.pending': 'Awaiting the clinic',
  'profile.clinics.status.active': 'Active',
  'profile.clinics.visitDuration': 'Slot {{count}} min',
  // The clinic scope switcher lives in the desktop SIDEBAR, which does not
  // exist below 768px — so on mobile it lives here, or a multi-clinic dentist is
  // locked to one scope forever with no way to say which.
  'profile.clinics.activeScope': 'Active clinic',
  'profile.clinics.activeScopeHint': 'Today and Patients show this clinic.',
  'profile.clinics.joinTitle': 'Join a clinic',
  'profile.clinics.joinHint': 'Ask the clinic manager for the invitation code.',
  'profile.clinics.joinPlaceholder': 'Invitation code',
  'profile.clinics.joined': 'You joined {{name}}.',
  'profile.clinics.notFound': 'No clinic has that invitation code.',
  'profile.clinics.joinFailed': 'Joining the clinic failed.',
  'profile.presence.empty': 'The clinic has not set shifts for you.',
  'profile.presence.oneOff': 'One-off',
  // A disabled shift was drawn at 55% opacity and nothing else — a state
  // carried by appearance alone, invisible to a screen reader and easy to miss.
  'profile.presence.inactive': 'Off',
  'profile.presence.readOnly': 'Shifts are set by the clinic; this panel reads them.',
  'profile.presence.days.0': 'Monday',
  'profile.presence.days.1': 'Tuesday',
  'profile.presence.days.2': 'Wednesday',
  'profile.presence.days.3': 'Thursday',
  'profile.presence.days.4': 'Friday',
  'profile.presence.days.5': 'Saturday',
  'profile.presence.days.6': 'Sunday',
  'profile.appearance.theme': 'Accent',
  'profile.appearance.fontSize': 'Text size',
  'profile.appearance.language': 'Language',
  'profile.appearance.saved': 'Preference saved.',
  'profile.appearance.failed': 'The preference was not saved on the server.',
  'profile.theme.default': 'Parsina blue',
  'profile.theme.teal': 'Turquoise',
  'profile.theme.ocean': 'Deep blue',
  'profile.theme.slate': 'Blue grey',
  'profile.fontSize.small': 'Small',
  'profile.fontSize.medium': 'Medium',
  'profile.fontSize.large': 'Large',
  'profile.language.fa': 'فارسی',
  'profile.language.en': 'English',
  'profile.language.fr': 'Français',
  'profile.language.de': 'Deutsch',

  // ── onboarding ───────────────────────────────────────────────────────────
  'onboarding.title': 'Parsina dental module',
  'onboarding.subtitle.step1': 'Register once so your account can be identified',
  'onboarding.subtitle.clinic': 'Connect to the clinic you work at',
  'onboarding.subtitle.done': 'You are ready',
  'onboarding.progress.step1': 'Registration',
  'onboarding.progress.clinic': 'Clinic',
  'onboarding.progress.done': 'Done',
  'onboarding.step1.fullName': 'Full name',
  'onboarding.step1.fullNamePlaceholder': 'Dr Maryam Ahmadi',
  'onboarding.step1.medicalCode': 'Medical council number',
  'onboarding.step1.medicalCodePlaceholder': 'e.g. 123456',
  'onboarding.step1.specialty': 'Specialty',
  'onboarding.step1.bio': 'About you (optional)',
  'onboarding.step1.submit': 'Continue',
  'onboarding.step1.specialtyNote': 'This panel is the dental module, so the specialty is set for you.',
  'onboarding.clinic.choiceTitle': 'Your practice or clinic',
  'onboarding.clinic.choiceDescription': 'If you have your own practice, register it here. If you work at another clinic, join it with an invitation code.',
  'onboarding.clinic.ownTitle': 'Register my own practice',
  'onboarding.clinic.ownBody': 'For a personal practice. You will be its owner.',
  'onboarding.clinic.joinTitle': 'Join an existing clinic',
  'onboarding.clinic.joinBody': 'With the invitation code the clinic gave you.',
  'onboarding.clinic.back': 'Back',
  'onboarding.clinic.ownName': 'Practice name',
  'onboarding.clinic.ownNamePlaceholder': 'Dr Ahmadi Dental Practice',
  'onboarding.clinic.ownCity': 'City (optional)',
  'onboarding.clinic.ownAddress': 'Address (optional)',
  'onboarding.clinic.ownPhone': 'Practice phone (optional)',
  'onboarding.clinic.create': 'Register practice',
  'validation.clinicName': 'Enter the practice name.',
  'onboarding.clinic.alreadyExists': 'A practice is already registered for this account.',
  // The currency the practice keeps its books in. `{{currency}}` is a name
  // with its ISO code, e.g. "UAE dirham (AED)".
  'onboarding.clinic.currency': 'Practice currency',
  'onboarding.clinic.currencyHint': 'Tariffs and treatment plans are recorded in this currency. It can be changed in the clinic panel only until the first amount is recorded.',
  'onboarding.clinic.currencyIrr': 'Toman (IRR)',
  'onboarding.clinic.currencyMismatch': 'A practice is already registered for this account, in {{currency}}. Choose that currency, or change it in the clinic panel.',
  'onboarding.clinic.setupIncomplete': 'The practice was created, but its currency could not be set in accounting. Do not register it again — contact Parsina support to finish the setup.',
  'onboarding.clinic.title': 'Join your clinic',
  'onboarding.clinic.description': 'With the clinic invitation code your appointments and the clinic register open up. You can do this later from your profile.',
  'onboarding.clinic.placeholder': 'Invitation code',
  'onboarding.clinic.join': 'Join',
  'onboarding.clinic.skip': 'Later',
  'onboarding.done.title': 'Welcome',
  'onboarding.done.body': 'Your dental workspace is ready. Today shows the appointments the clinic books for you.',
  'onboarding.done.enter': 'Enter the panel',

  // ── validation ───────────────────────────────────────────────────────────
  'validation.medicalCode': 'Enter your medical council number.',
  'validation.fullName': 'Enter your full name.',
  'validation.invitationCode': 'Enter the invitation code.',
  'validation.officePhone': 'The practice phone is not a complete number. Correct it, or clear the field.',
  // The kit's PhoneInput copy, in this app's two languages. `{{example}}`
  // arrives already wrapped in FSI/PDI, so it reads LTR inside a Persian line.
  'validation.phone.tooShort': 'This {{country}} number is short — example: {{example}}',
  'validation.phone.tooLong': 'This {{country}} number is long — example: {{example}}',
  'validation.phone.invalid': 'That is not a valid number.',

  // ── چارت دندان / odontogram ──────────────────────────────────────────────
  'patient.tabs.plan': 'Treatment plan',
  'patient.plan.empty.title': 'No treatment plan has been recorded for this patient',
  'patient.plan.empty.description': 'A treatment plan is written during a visit.',
  'patient.plan.noLines': 'This course has no lines yet.',
  'patient.plan.doneTitle': 'Treatments performed',
  'patient.plan.outstanding': '{{count}} treatments outstanding \u2014 {{amount}} in total',
  'patient.tabs.chart': 'Dental chart',
  'chart.tooth': 'Tooth',
  'chart.universalShort': 'US #',
  'chart.annotation.planned': 'planned',
  'chart.annotation.treated': 'treated',
  'chart.legend.planned': 'Planned',
  'chart.legend.treated': 'Treated',
  'chart.tooltip.treated': 'Treatments performed',
  'chart.tooltip.andMore': 'and {{count}} more',
  'chart.tooltip.planned': 'Planned',
  'chart.wholeMouth.title': 'Whole mouth',
  'chart.history.title': 'History of tooth {{fdi}}',
  'chart.history.treatment': 'Treatment',
  'chart.history.event': 'Chart',
  'chart.history.causedBy': 'from the treatment above',
  'chart.history.empty': 'Nothing has been recorded for this tooth yet.',
  'chart.history.unavailable': 'This tooth\u2019s history could not be read.',
  'chart.history.session': 'Session {{n}}',
  'chart.history.voided': 'Voided — {{reason}}',
  'chart.notCharted': 'Not charted',
  'chart.patientRight': "PATIENT'S RIGHT",
  'chart.patientLeft': "PATIENT'S LEFT",
  'chart.aria.odontogram': 'Dental chart — click a tooth or a surface to record',
  'chart.hint': 'Click a surface to record it, or the tooth for its whole state.',
  // ── chart, mobile ────────────────────────────────────────────────────────
  // `chart.hint` names a MOUSE action. On a touch screen a crown is ~10px and a
  // surface far less, which is under the 44px floor and cannot be fixed inside
  // the arch without breaking the anatomical frame — so the phone gets its own
  // wording and its own ≥44px tooth picker beneath the chart.
  'chart.mobile.settings': 'Chart settings',
  'chart.mobile.pickTooth': 'Pick a tooth',
  'chart.mobile.tapHint': 'Tap a tooth on the chart, or pick one below — surfaces are recorded in the sheet that opens.',
  'chart.mobile.legend': 'Key',
  'chart.legend.bridge': 'Bridge',
  'chart.legend.wholeToothCaries': 'Whole-tooth caries',
  'chart.export.button': 'Export image',
  'chart.export.error': 'Could not export the image — this browser does not support it.',
  'chart.material': 'Material',
  'chart.isHistorical': 'Existing work (not done today)',
  'chart.showPrimary': 'Show primary teeth',
  'chart.hidePrimary': 'Hide primary teeth',
  'chart.showUniversal': 'Show US numbers',
  'chart.hideUniversal': 'Hide US numbers',
  'chart.error.title': 'Could not load the chart',
  'chart.error.body': 'The dental chart could not be fetched. Please try again.',
  'chart.summary.charted': 'Charted',
  'chart.summary.decayed': 'Decayed',
  'chart.summary.restored': 'Restored',
  'chart.summary.missing': 'Missing',
  'chart.summary.dmft': 'DMFT',
  'chart.presence.title': 'Presence',
  'chart.presence.present': 'Present',
  'chart.presence.extracted': 'Extracted',
  'chart.presence.exfoliated': 'Exfoliated',
  'chart.presence.congenitally_absent': 'Congenitally absent',
  'chart.presence.unerupted': 'Unerupted',
  'chart.presence.impacted': 'Impacted',
  'chart.surfaces.title': 'Surfaces',
  'chart.surfaces.hint': 'Pick a surface, then its state',
  'chart.surfaces.setFor': 'State for',
  'chart.surface.M': 'Mesial',
  'chart.surface.D': 'Distal',
  'chart.surface.F': 'Facial',
  'chart.surface.L': 'Lingual',
  'chart.surface.O': 'Occlusal',
  'chart.surface.I': 'Incisal',
  'chart.surfaceState.sound': 'Sound',
  'chart.surfaceState.caries': 'Caries',
  'chart.surfaceState.restoration': 'Restoration',
  'chart.surfaceState.recurrent_caries': 'Recurrent caries',
  'chart.surfaceState.temporary': 'Temporary',
  'chart.surfaceState.sealant': 'Sealant',
  'chart.surfaceState.fractured': 'Fractured',
  'chart.condition.title': 'Whole tooth',
  'chart.condition.hint': 'Independent of the surfaces above',
  'chart.conditionName.sound': 'Sound',
  'chart.conditionName.caries': 'Caries',
  'chart.conditionName.root_canal': 'Root canal',
  'chart.conditionName.post_core': 'Post & core',
  'chart.conditionName.crown': 'Crown',
  'chart.conditionName.veneer': 'Veneer',
  'chart.conditionName.implant': 'Implant',
  'chart.conditionName.bridge_abutment': 'Bridge abutment',
  'chart.conditionName.bridge_pontic': 'Pontic',
  'chart.conditionName.fracture': 'Fracture',
  'chart.conditionName.attrition': 'Attrition',
  'chart.conditionName.mobility': 'Mobility',
  'chart.conditionName.periapical_lesion': 'Periapical lesion',
  'chart.conditionName.sealant': 'Sealant',
  'chart.conditionName.temporary': 'Temporary',
  'chart.conditionName.watch': 'Watch',
  'chart.note.title': 'Note',
  'chart.note.placeholder': 'Anything worth recording about this tooth',
  'chart.materialName.composite': 'Composite',
  'chart.materialName.amalgam': 'Amalgam',
  'chart.materialName.glass_ionomer': 'Glass ionomer',
  'chart.materialName.zirconia': 'Zirconia',
  'chart.materialName.pfm': 'PFM',
  'chart.materialName.full_metal': 'Full metal',
  'chart.materialName.emax': 'E.max',
  'chart.materialName.temporary': 'Temporary',
  'chart.materialName.gold': 'Gold',

  // ── hover card ──────────────────────────────────────────────────────────
  'chart.primary': 'primary',
  'chart.tooltip.notCharted': 'Nothing recorded for this tooth yet.',
  'chart.tooltip.sound': 'Examined — no findings',
  'chart.tooltip.lastRecorded': 'Last recorded',
  'chart.quadrant.upperRight': "Upper right (patient's)",
  'chart.quadrant.upperLeft': "Upper left (patient's)",
  'chart.quadrant.lowerRight': "Lower right (patient's)",
  'chart.quadrant.lowerLeft': "Lower left (patient's)",
  'chart.toothName.centralIncisor': 'Central incisor',
  'chart.toothName.lateralIncisor': 'Lateral incisor',
  'chart.toothName.canine': 'Canine',
  'chart.toothName.firstPremolar': 'First premolar',
  'chart.toothName.secondPremolar': 'Second premolar',
  'chart.toothName.firstMolar': 'First molar',
  'chart.toothName.secondMolar': 'Second molar',
  'chart.toothName.thirdMolar': 'Third molar (wisdom)',

  // ── جلسهٔ درمان — the visit workspace (docs/dent-visit.md) ────────────────
  //
  // Two columns: a persistent clinical view (odontogram · subjective · طرح
  // درمان) and a tabbed workspace (دارو · دستورات · تشخیص · پرسش · فایل‌ها).

  'visit.error.title': 'The visit could not be opened',
  'visit.error.body': 'We could not read this appointment. Please try again.',
  'visit.notFound.title': 'Visit not found',
  'visit.notFound.description':
    'This appointment does not exist, is not yours, or has no patient record attached.',
  'visit.unknownPatient': 'Unnamed patient',
  'visit.openRecord': 'Patient record',
  'visit.finish.action': 'Finish visit',
  'visit.finish.working': 'Closing…',
  'visit.finish.confirm.title': 'Close this visit?',
  'visit.finish.confirm.body': 'Anything unsaved is written and the visit is closed. After that it is read-only and this app cannot reopen it.',
  // «نوبت بعدی» — asked only when the visit leaves work planned and not done.
  'visit.finish.followUp.title': '{{count}} treatment(s) left in the plan',
  'visit.finish.followUp.andMore': 'and {{count}} more',
  'visit.finish.followUp.today': 'Today',
  'visit.finish.followUp.tomorrow': 'Tomorrow',
  'visit.finish.followUp.withinWeek': 'Within a week',
  'visit.finish.followUp.within2Weeks': 'Within two weeks',
  'visit.finish.followUp.withinMonth': 'Within a month',
  'visit.finish.followUp.within3Months': 'Within three months',
  'visit.finish.followUp.when': 'By when should the next appointment be?',
  'visit.finish.followUp.none': 'Not needed',
  'visit.finish.followUp.deskNote': 'Reception will be told, so they can book it with you.',
  'visit.finish.followUp.noneNote': 'Reception will not be told anything.',
  'visit.finish.done.plain': 'The visit is closed.',
  'visit.finish.done.followUpRaised': 'The visit is closed. Reception has been asked to book the next one.',
  'visit.finish.done.alreadyBooked': 'The visit is closed. This patient already has a next appointment.',
  'visit.finish.done.followUpFailed': 'The visit is closed, but reception was not told about the next appointment.',
  'visit.finish.error.subjective': 'The note was not saved, so the visit was not closed. Try again.',
  'visit.finish.error.complete': 'The note was saved, but the visit could not be closed. Try again.',

  // A finished visit is READ-ONLY: this app cannot reopen an appointment, so a
  // late write would file a record under a day that is over.
  // ── The video visit ──────────────────────────────────────────────────────
  'visit.call.start': 'Video call',
  'visit.call.title': 'Video call',
  'visit.call.panelLabel': 'Video call panel',
  'visit.call.connecting': 'Connecting…',
  // "End call", never "End visit" — closing the visit is one-way and closes the
  // record; hanging up does neither, and the two must never read alike.
  'visit.call.hangUp': 'End call',
  'visit.call.notRecorded': 'This call is not recorded and nothing from it is stored.',
  'visit.call.error.connection': 'The call could not connect. Check the connection and try again.',
  'visit.call.error.unavailable': 'Video visits are not enabled in this environment.',
  'visit.call.error.visitOver': 'This visit is closed, so a video call cannot be placed on it.',

  'visit.readOnly.badge': 'Read-only',
  'visit.readOnly.note':
    'This visit is closed, so nothing further can be recorded against it. Everything already on record is still shown.',

  // Every AI action is a button, never automatic — each one bills a credit.
  'visit.ai.costsCredits': 'Uses AI credit.',

  'visit.tabs.meds': 'Medication',
  'visit.tabs.orders': 'Orders',
  'visit.tabs.diagnosis': 'Diagnosis',
  'visit.tabs.questions': 'Questions',
  'visit.tabs.files': 'Files',
  'visit.tabs.sectionsLabel': 'Visit sections',

  // ── §3.1.1 odontogram ───────────────────────────────────────────────────
  'visit.chart.title': 'Dental chart',
  'visit.chart.hint': 'Select a tooth to see its findings and what is planned on it.',
  'visit.chart.tooth': 'Tooth {{fdi}}',
  'visit.chart.openTooth': 'Findings',
  'visit.chart.planForTooth': 'Add to plan',
  'visit.chart.noPlanForTooth': 'Nothing planned on this tooth.',
  'visit.chart.history': 'History',
  'visit.chart.noHistory': 'Nothing has been recorded for this tooth.',
  'visit.chart.historyUnavailable': 'The history could not be loaded.',
  'visit.chart.historical': 'pre-existing',

  // ── §3.1.2 subjective ───────────────────────────────────────────────────
  'visit.subjective.title': 'Patient subjective',
  'visit.subjective.error.title': 'The note could not be loaded',
  'visit.subjective.error.body': 'We could not read this visit’s note. Please try again.',
  'visit.subjective.saved': 'Saved',
  'visit.subjective.saveFailed': 'The note could not be saved. Please try again.',
  'visit.subjective.history': 'Patient history',
  'visit.subjective.noAllergies': 'No allergies on record',
  'visit.subjective.conditions': 'Conditions',
  'visit.subjective.medications': 'Medication',
  'visit.subjective.chiefComplaint': 'Chief complaint',
  'visit.subjective.dictateComplaint': 'Dictate the chief complaint',
  'visit.subjective.dictateNotes': 'Dictate the note',
  'visit.subjective.teeth': 'Teeth involved',
  'visit.subjective.noTeeth': 'Tap the teeth on the chart above',
  'visit.subjective.addTooth': 'Add tooth {{fdi}}',
  'visit.subjective.selectToothFirst': 'Select a tooth on the chart',
  'visit.subjective.painScore': 'Pain (0–10)',
  'visit.subjective.painCharacter': 'Pain character',
  'visit.subjective.pain.sharp': 'Sharp',
  'visit.subjective.pain.dull': 'Dull',
  'visit.subjective.pain.throbbing': 'Throbbing',
  'visit.subjective.pain.sensitivity': 'Sensitivity',
  'visit.subjective.pain.none': 'No pain',
  'visit.subjective.onset': 'Onset',
  'visit.subjective.duration': 'Duration',
  'visit.subjective.aggravating': 'Made worse by',
  'visit.subjective.relieving': 'Relieved by',
  'visit.subjective.dentalHistory': 'Dental history',
  'visit.subjective.notes': 'Notes',
  'visit.subjective.priorVisits': 'Previous visits',
  'visit.subjective.noPriorVisits': 'No previous visits on record.',
  'visit.subjective.priorNoSummary': 'No complaint recorded',
  'visit.subjective.priorDiagnoses': '{{count}} diagnoses',

  // ── §3.1.3 treatment plan ───────────────────────────────────────────────
  'visit.plan.title': 'Treatment plan',
  'visit.plan.error.title': 'The plan could not be loaded',
  'visit.plan.error.body': 'We could not read this patient’s treatment plan. Please try again.',
  // Clinic-currency refusals, by extensions.code (CLINIC_CURRENCY_UNAVAILABLE,
  // CURRENCY_MISMATCH). Never an exchange rate: two currencies are not combined.
  'visit.plan.error.currencyUnavailable': 'The practice’s currency could not be read, and without it the plan’s amounts cannot be shown. Please try again.',
  'visit.plan.error.currencyMismatch': 'These amounts are not in the practice’s currency, and amounts in different currencies are never added together. Ask the practice manager to check the practice’s currency.',
  'visit.plan.saveFailed': 'The plan could not be saved.',
  'visit.plan.empty.title': 'No plan yet',
  'visit.plan.empty.description': 'Add the treatments you intend to carry out, tooth by tooth.',
  'visit.plan.add': 'Add treatment',
  'visit.plan.added': '“{{what}}” was added to the treatment plan.',
  'visit.plan.addedForTooth': '“{{what}}” was added to the treatment plan for tooth {{fdi}}.',
  'visit.chart.planFormOpened': 'Add form opened for tooth {{fdi}} — choose the treatment.',
  'visit.plan.addItem': 'Add to plan',
  'visit.plan.service': 'Treatment',
  'visit.plan.choose': 'Choose a treatment…',
  'visit.plan.tooth': 'Tooth',
  'visit.plan.wholeMouth': 'Whole mouth',
  'visit.plan.wholeMouthShort': 'Mouth',
  'visit.plan.phase': 'Phase',
  'visit.plan.description': 'Description',
  'visit.plan.untitledItem': 'Untitled treatment',
  'visit.plan.changeStatus': 'Change status of {{item}}',
  'visit.plan.remove': 'Remove {{item}}',
  'visit.plan.showOnChart': 'Show on the chart',
  // Approval is the one irreversible call: amounts become whole rials once, the
  // totals are computed and the signed document is frozen.
  'visit.plan.surfaces': 'Surfaces',
  'visit.plan.sessions': 'Sessions',
  'visit.plan.sessionsOf': 'Session {{done}} of {{total}}',
  // Key kept from when every amount was toman. `{{unit}}` is "toman" for IRR
  // (common.units.toman), and the ISO code — "AED" — for any other currency.
  'visit.plan.toman': '{{amount}} {{unit}}',
  'visit.plan.noTariff': 'No tariff set',
  'visit.plan.outstanding': 'Outstanding: {{amount}}',
  // A service the clinic prices in another currency than the plan's. It is
  // never converted, so it is never priced into this plan. `{{currency}}` and
  // `{{planCurrency}}` are ISO codes ("IRR", "AED"): a name would put "toman"
  // on a plan counted in dirhams.
  'visit.plan.otherCurrency': 'Tariff in another currency ({{currency}})',
  'visit.plan.otherCurrencyRefused': 'The tariff of “{{name}}” is in {{currency}}, so it cannot be added to a plan in {{planCurrency}}.',

  // «ثبت انجام» — what was actually DONE, as against what was quoted.
  'visit.record.openVisit': 'Open visit',
  'visit.record.open': 'Record as done',
  'visit.record.openSession': 'Record session',
  'visit.record.title': 'Record as done — {{treatment}}',
  'visit.record.subtitle': 'Records what was done in this visit. This is the clinical record.',
  'visit.record.service': 'Treatment performed',
  'visit.record.tooth': 'Tooth',
  'visit.record.surfaces': 'Surfaces',
  'visit.record.material': 'Material',
  'visit.record.noMaterial': 'No material',
  'visit.record.note': 'What was done',
  'visit.record.notePlaceholder': 'Briefly, what was done',
  'visit.record.outcome': 'Outcome',
  'visit.record.outcome.completed': 'Completed',
  'visit.record.outcome.inProgress': 'Continues next session',
  'visit.record.chartEffect': 'Tooth state after treatment',
  'visit.record.chartEffect.none': 'Leave the chart unchanged',
  'visit.record.chartEffect.noneDetail': 'Nothing will be written to the dental chart.',
  'visit.record.chartEffect.detail': 'Tooth {{tooth}} will be charted as “{{state}}”.',
  'visit.record.chartEffect.detailSurfaces': 'Surfaces {{surfaces}} of tooth {{tooth}} will be charted as “{{state}}”.',
  'visit.record.chartEffect.needsSurfaces': 'Pick the surfaces that were treated, or nothing will be written to the chart.',
  'visit.record.chartEffect.surfaceOption': 'Surfaces — {{state}}',
  'visit.record.chartEffect.inProgress': 'This session does not finish the treatment, so by default nothing is charted.',
  'visit.record.billingNote':
    'Billing and stock are separate writes made after this one; this form records no amount.',
  'visit.record.save': 'Record',
  'visit.record.saved': '“{{what}}” recorded.',
  'visit.record.failed': 'The treatment could not be recorded.',
  'visit.record.doneHere': 'Done in this visit ({{count}})',
  'visit.record.void': 'Void',
  'visit.record.voidReason': 'Reason for voiding',
  'visit.record.voidedFor': 'Voided — {{reason}}',
  'visit.record.voidConfirm.title': 'Void “{{what}}”?',
  'visit.record.voidConfirm.body':
    'The row is not deleted: it stays in the record, struck through, with the reason you give. A reason is required.',
  'visit.record.notBilled': 'Not on the bill',
  'visit.record.voidConfirm.alsoChart': 'Also void what this wrote to tooth {{tooth}} on the chart',
  'visit.record.voidConfirm.stillBilled':
    'This treatment is already charged on the patient\u2019s invoice. Voiding it takes the charge back automatically \u2014 as a negative correction line if the invoice has been issued.',
  'visit.record.voidCharge.notReversed':
    'The treatment was voided, but its charge could not be taken off the invoice. Ask the cash desk to correct it.',
  'visit.record.billed': 'Added to this visit\u2019s invoice.',

  'visit.plan.noServices.title': 'This clinic has not defined any services yet',
  'visit.plan.noServices.body':
    'Treatment plan lines come from the clinic\u2019s services. Define and price the dental services in the clinic panel.',
  'visit.plan.noServices.action': 'Define services in the clinic panel',
  'visit.plan.catalogUnavailable':
    'The clinic\u2019s service list could not be read. This does not mean there are none \u2014 try again.',
  'visit.plan.applicability.unset': 'Not specified',
  'visit.plan.applicability.tooth': 'One tooth',
  'visit.plan.applicability.surfaces': 'Surfaces of one tooth',
  'visit.plan.applicability.quadrant': 'One quadrant',
  'visit.plan.applicability.arch': 'One arch',
  'visit.plan.applicability.mouth': 'Whole mouth',
  'visit.plan.newService.title': 'New service',
  'visit.plan.newService.subtitle':
    'Added to the clinic\u2019s services, and offered to every colleague from then on.',
  'visit.plan.newService.name': 'Service name',
  'visit.plan.newService.category': 'Group',
  'visit.plan.newService.chartEffect': 'Effect on the chart',
  'visit.plan.newService.applicability': 'Applies to',
  'visit.plan.newService.tariff': 'Tariff per session ({{unit}})',
  'visit.plan.newService.duration': 'Minutes per session',
  'visit.plan.newService.tariffHint':
    'The tariff may be left empty; the service is saved with no tariff and is not billed until one is set.',
  'visit.plan.newService.clinicOnly.title': 'Completed in the clinic panel',
  'visit.plan.newService.clinicOnly.body':
    'Consumables, other costs, commission and insurance.',
  'visit.plan.newService.save': 'Save and select',
  'visit.plan.newService.created': '\u201c{{name}}\u201d was added to the clinic\u2019s services.',
  'visit.plan.newService.nameRequired': 'A service name is required.',
  'visit.plan.newService.failed': 'The service could not be saved.',
  'visit.plan.newService.badTariff': 'The tariff must be a number, in {{unit}}.',
  'visit.plan.newService.currencyUnknown': 'This practice’s currency could not be read, so the tariff cannot be entered here. The service is saved with no tariff; set it in the clinic panel.',
  'visit.plan.noClinic':
    'This visit is not attached to a clinic; without one, services and tariffs cannot be read.',
  'common.form.optional': '(optional)',
  'visit.plan.locked': 'Signed',
  'visit.plan.startNew': 'Start a new course',
  'visit.plan.lockedNextStep': 'Adding a treatment starts a new course.',
  'visit.plan.lockedNote':
    'This plan has been approved and signed, so its lines can no longer be changed. Treatments can still be marked as done.',
  'visit.plan.phases.emergency': 'Emergency',
  'visit.plan.phases.phase1': 'Phase 1',
  'visit.plan.phases.phase2': 'Phase 2',
  'visit.plan.phases.maintenance': 'Maintenance',
  // The service's five item states. The document names three (Pending,
  // Scheduled, Completed); these are the ones that actually get stored.
  'visit.plan.status.proposed': 'Proposed',
  'visit.plan.status.accepted': 'Accepted',
  'visit.plan.status.done': 'Done',
  'visit.plan.status.rejected': 'Declined',
  'visit.plan.status.deferred': 'Deferred',
  'visit.plan.planStatus.draft': 'Draft',
  'visit.plan.planStatus.proposed': 'Proposed',
  'visit.plan.planStatus.approved': 'Approved',
  'visit.plan.planStatus.in_progress': 'In progress',
  'visit.plan.planStatus.completed': 'Completed',
  'visit.plan.planStatus.abandoned': 'Abandoned',

  // ── §3.2.1 medication ───────────────────────────────────────────────────
  'visit.meds.empty.title': 'Nothing prescribed yet',
  'visit.meds.empty.description': 'Search the formulary above to add a medication to this visit.',
  'visit.meds.thisVisit': 'Prescribed in this visit',
  'visit.meds.current': 'Current medication',
  'visit.meds.suggest': 'AI suggestions',
  'visit.meds.suggesting': 'Analysing…',
  'visit.meds.suggestions': 'Suggested medication',
  'visit.meds.suggestClose': 'Dismiss suggestions',
  'visit.meds.suggestAdd': 'Add to prescription',
  'visit.meds.suggestAdded': 'Added',
  'visit.meds.noSuggestions':
    'Nothing to suggest. Record this visit\u2019s diagnosis or assessment first \u2014 the analysis reads what has been written for this visit.',
  'visit.meds.suggestUnavailable': 'AI suggestions are unavailable.',
  'visit.meds.followUp': 'Follow-up:',

  // ── §3.2.2 orders — dental lab and imaging only ─────────────────────────
  'visit.orders.error.title': 'Orders could not be loaded',
  'visit.orders.error.body': 'We could not read this visit’s orders. Please try again.',
  'visit.orders.saveFailed': 'The order could not be saved.',
  'visit.orders.empty.title': 'No orders in this visit',
  'visit.orders.empty.description': 'Request a radiograph or send a case to the lab.',
  'visit.orders.type': 'Order type',
  'visit.orders.types.imaging': 'Imaging',
  'visit.orders.types.lab': 'Laboratory',
  'visit.orders.types.dental_lab': 'Dental laboratory',
  'visit.orders.service': 'Service',
  'visit.orders.serviceScope': '“{{type}}” services only',
  'visit.orders.choose': 'Choose a service…',
  'visit.orders.description': 'Description',
  'visit.orders.descriptionHint': 'Leave blank to use the service name',
  'visit.orders.notes': 'Notes for the lab / radiology',
  'visit.orders.urgency': 'Urgency',
  'visit.orders.urgencies.routine': 'Routine',
  'visit.orders.urgencies.urgent': 'Urgent',
  'visit.orders.urgencies.stat': 'Immediate',
  'visit.orders.sessions': 'Sessions',
  'visit.orders.sessionCount': '{{count}} sessions',
  'visit.orders.add': 'Place order',
  'visit.orders.statuses.pending': 'Pending',
  'visit.orders.statuses.in_progress': 'In progress',
  'visit.orders.statuses.completed': 'Completed',
  'visit.orders.statuses.cancelled': 'Cancelled',
  'visit.orders.transitions.start': 'Start',
  'visit.orders.transitions.complete': 'Mark done',
  'visit.orders.transitions.cancel': 'Cancel',
  'visit.orders.suggest': 'AI suggestions',
  'visit.orders.suggesting': 'Analysing…',
  'visit.orders.suggestions': 'Suggested orders',
  'visit.orders.suggestClose': 'Dismiss suggestions',
  'visit.orders.suggestAdd': 'Place order',
  'visit.orders.suggestAdded': 'Placed',
  'visit.orders.noSuggestions':
    'Nothing to suggest. Record this visit\u2019s diagnosis or assessment first \u2014 the analysis reads what has been written for this visit.',
  'visit.orders.suggestUnavailable': 'AI suggestions are unavailable.',
  'visit.orders.suggestDropped': '{{count}} suggestion(s) fell outside the dental catalogue and were not shown.',

  // ── §3.2.3 diagnosis, with click-to-reveal traceability ─────────────────
  'visit.diagnosis.error.title': 'Diagnoses could not be loaded',
  'visit.diagnosis.error.body': 'We could not read this visit’s diagnoses. Please try again.',
  'visit.diagnosis.saveFailed': 'The diagnosis could not be saved.',
  'visit.diagnosis.empty.title': 'No diagnosis recorded',
  'visit.diagnosis.empty.description':
    'Record what you have concluded, and the findings it rests on are stored with it.',
  'visit.diagnosis.name': 'Diagnosis',
  'visit.diagnosis.code': 'ICD code',
  'visit.diagnosis.certainty': 'Certainty',
  'visit.diagnosis.verification.provisional': 'Provisional',
  'visit.diagnosis.verification.confirmed': 'Confirmed',
  'visit.diagnosis.add': 'Record diagnosis',
  'visit.diagnosis.remove': 'Remove {{name}}',
  'visit.diagnosis.willAttach': '{{count}} supporting findings will be stored with this diagnosis.',
  // The disclosure §3.2.3 requires: every entry reveals what it was derived from.
  'visit.diagnosis.evidence': 'Evidence ({{count}})',
  'visit.diagnosis.noEvidence': 'No supporting findings were recorded with this diagnosis.',
  'visit.diagnosis.evidenceKind.subjective': 'Subjective',
  'visit.diagnosis.evidenceKind.history': 'History',
  'visit.diagnosis.evidenceKind.chart': 'Chart',
  'visit.diagnosis.evidenceKind.file': 'Document',
  'visit.diagnosis.evidenceKind.order': 'Order',
  'visit.diagnosis.evidenceKind.ai': 'AI analysis',
  'visit.diagnosis.showTooth': 'Show tooth',
  'visit.diagnosis.toothEvidence': 'Findings on tooth {{fdi}}',
  'visit.diagnosis.aiAssisted': 'AI-assisted',
  'visit.diagnosis.suggest': 'AI suggestions',
  'visit.diagnosis.suggesting': 'Analysing…',
  'visit.diagnosis.suggestions': 'Suggested diagnoses',
  'visit.diagnosis.noSuggestions':
    'No suggestions. Record the subjective first — the analysis reads what has been written for this visit.',
  'visit.diagnosis.suggestUnavailable': 'AI suggestions are unavailable right now.',
  'visit.diagnosis.accept': 'Record',
  'visit.diagnosis.alreadyRecorded': 'Recorded',
  'visit.diagnosis.confidence.high': 'High confidence',
  'visit.diagnosis.confidence.medium': 'Medium confidence',
  'visit.diagnosis.confidence.low': 'Low confidence',

  // ── SOAP assistant — the diagnosis tab ──────────────────────────────────
  'visit.soap.title': 'Note assistant',
  // "When you stop", not "then press the button": there is no separate button
  // any more — stopping the recording sends the analysis, and the stop
  // button's own label says so.
  'visit.soap.description':
    'Say what happened in the session; when you stop, the assistant completes the note sections and advises on the problem. Nothing is recorded until you approve it.',
  // Not 'Dictate the note' — visit.subjective.dictateNotes already owns that
  // accessible name, and two controls answering to it breaks anyone querying
  // by role.
  'visit.soap.speak': 'Dictate the session',
  'visit.soap.speakStop': 'Finish and get advice',
  'visit.soap.analyzing': 'Analyzing…',
  'visit.soap.heard': 'What was heard:',
  'visit.soap.retry': 'Send again',
  'visit.soap.voiceUnavailable':
    'Dictation needs a microphone and a secure (HTTPS) connection, and is not available in this browser.',
  'visit.soap.draftTitle': 'Proposed note draft',
  'visit.soap.sections.s': 'Subjective (S) — appended to notes',
  'visit.soap.sections.o': 'Examination & findings (O)',
  'visit.soap.sections.a': 'Assessment (A)',
  'visit.soap.sections.p': 'Treatment plan (P)',
  'visit.soap.apply': 'Apply to the patient note',
  'visit.soap.applied': 'Written into the patient note.',
  'visit.soap.applyFailed': 'Applying the draft failed.',
  'visit.soap.dismiss': 'Dismiss',
  'visit.soap.noNews': 'Nothing new to add to the note.',
  'visit.soap.refineUnavailable': 'Note completion is unavailable.',
  'visit.soap.adviceTitle': 'Assistant advice',
  'visit.soap.adviceUnavailable': 'AI advice is unavailable.',
  'visit.soap.suggestedDiagnosis': 'Suggested diagnosis',
  'visit.soap.recordDiagnosis': 'Record as diagnosis',
  'visit.soap.accepted': 'Accepted — recorded in this visit’s diagnoses.',
  'visit.soap.actions': 'Suggested actions',
  'visit.soap.medications': 'Suggested medications',
  'visit.soap.aiEvidence': 'Derived from the session narrative analysis',

  // ── Per-tooth findings from the narrative → the dental chart ──────────────
  'visit.soap.teeth.title': 'Tooth findings',
  'visit.soap.teeth.description':
    'These were read out of the session narrative as being about specific teeth. Whichever you confirm is recorded on the dental chart; the rest are not recorded.',
  'visit.soap.teeth.lowConfidenceHint':
    'Low-confidence rows are left unticked — check the tooth number before recording.',
  'visit.soap.teeth.tooth': 'Tooth {{fdi}}',
  'visit.soap.teeth.surface': 'Surface {{surface}}',
  'visit.soap.teeth.historical': 'History',
  'visit.soap.teeth.record': 'Record on chart ({{count}})',
  'visit.soap.teeth.recording': 'Recording…',
  'visit.soap.teeth.recorded': 'Recorded on the dental chart.',
  'visit.soap.teeth.recordFailed': 'Could not record on the dental chart.',
  'visit.soap.teeth.none': 'No specific tooth finding was found in the narrative.',
  'visit.soap.teeth.unavailable': 'Reading tooth findings is unavailable.',
  'visit.soap.teeth.confidence.high': 'High confidence',
  'visit.soap.teeth.confidence.medium': 'Medium confidence',
  'visit.soap.teeth.confidence.low': 'Low confidence',

  'tooth.aspect.presence': 'Tooth presence',
  'tooth.aspect.condition': 'Tooth condition',
  'tooth.aspect.surface': 'Surface state',
  'tooth.value.sound': 'Sound',
  'tooth.value.caries': 'Caries',
  'tooth.value.root_canal': 'Root canal',
  'tooth.value.post_core': 'Post and core',
  'tooth.value.crown': 'Crown',
  'tooth.value.veneer': 'Veneer',
  'tooth.value.implant': 'Implant',
  'tooth.value.bridge_abutment': 'Bridge abutment',
  'tooth.value.bridge_pontic': 'Pontic',
  'tooth.value.fracture': 'Fracture',
  'tooth.value.attrition': 'Attrition',
  'tooth.value.mobility': 'Mobility',
  'tooth.value.periapical_lesion': 'Periapical lesion',
  'tooth.value.sealant': 'Sealant',
  'tooth.value.temporary': 'Temporary',
  'tooth.value.watch': 'Watch',
  'tooth.value.restoration': 'Restoration',
  'tooth.value.recurrent_caries': 'Recurrent caries',
  'tooth.value.fractured': 'Fractured',
  'tooth.value.present': 'Present',
  'tooth.value.extracted': 'Extracted',
  'tooth.value.exfoliated': 'Exfoliated',
  'tooth.value.congenitally_absent': 'Congenitally absent',
  'tooth.value.unerupted': 'Unerupted',
  'tooth.value.impacted': 'Impacted',
  'tooth.material.composite': 'Composite',
  'tooth.material.amalgam': 'Amalgam',
  'tooth.material.glass_ionomer': 'Glass ionomer',
  'tooth.material.zirconia': 'Zirconia',
  'tooth.material.pfm': 'PFM',
  'tooth.material.full_metal': 'Full metal',
  'tooth.material.emax': 'E.max',
  'tooth.material.temporary': 'Temporary',
  'tooth.material.gold': 'Gold',

  // ── §3.2.4 questions — the clinical assistant ───────────────────────────
  'visit.questions.title': 'Clinical assistant',
  'visit.questions.forDiagnoses': 'Questions that would confirm or exclude: {{list}}',
  'visit.questions.noDiagnosesYet':
    'No diagnosis recorded yet — the assistant will ask about the complaint you have written.',
  'visit.questions.generate': 'Find the gaps',
  'visit.questions.generating': 'Thinking…',
  'visit.questions.unavailable': 'The clinical assistant is unavailable right now.',
  'visit.questions.fallbackNote':
    'These are general questions rather than ones written for this patient.',
  'visit.questions.empty.title': 'No open questions',
  'visit.questions.empty.description': 'Nothing further is needed to support the current diagnoses.',
  'visit.questions.count': '{{count}} questions',
  'visit.questions.yes': 'Yes',
  'visit.questions.no': 'No',
  // Answering RECORDS a finding into the note — that is what makes this a
  // workflow rather than a list of prompts.
  'visit.questions.recorded': 'Recorded: {{statement}}',
  'visit.questions.recordFailed': 'The answer could not be recorded in the note.',
  'visit.questions.askTitle': 'Ask the assistant',
  'visit.questions.askPlaceholder': 'Ask about this patient…',
  'visit.questions.ask': 'Ask',
  'visit.questions.asking': 'Asking…',
  'visit.questions.followUps': 'Follow-up',

  // ── §3.2.5 files, and the report each one produced ──────────────────────
  // ── فایل‌ها: what kind of thing is being uploaded ────────────────────────
  'visit.files.kind': 'What is this?',
  'visit.files.kinds.image': 'Dental image',
  'visit.files.kinds.document': 'Document',
  'visit.files.viewType': 'View',
  'visit.files.viewTypes.periapical': 'Periapical (PA)',
  'visit.files.viewTypes.bitewing': 'Bitewing (BW)',
  'visit.files.viewTypes.panoramic': 'Panoramic (OPG)',
  'visit.files.viewTypes.ceph': 'Cephalometric',
  'visit.files.viewTypes.photo': 'Photograph',
  'visit.files.plainUploadNote': 'Uploading only stores the file. Text extraction and image reading are separate actions.',
  'visit.files.unsupportedImage': 'This image format cannot be displayed. Use JPEG or PNG.',
  'visit.files.openImage': 'Open image',
  'visit.files.extract': 'Extract text',
  'visit.files.extracting': 'Extracting…',

  // ── The image workspace ─────────────────────────────────────────────────
  'visit.image.back': 'Back to files',
  'visit.image.tools': 'Drawing tools',
  'visit.image.tool.cursor': 'Select',
  'visit.image.tool.pin': 'Pin',
  'visit.image.tool.box': 'Box',
  'visit.image.tool.ellipse': 'Circle',
  'visit.image.tool.arrow': 'Arrow',
  'visit.image.tool.freehand': 'Freehand',
  'visit.image.color': 'Pen colour',
  'visit.image.colorName.E11D48': 'Red',
  'visit.image.colorName.F59E0B': 'Amber',
  'visit.image.colorName.0EA5A0': 'Teal',
  'visit.image.colorName.2563EB': 'Blue',
  'visit.image.zoomIn': 'Zoom in',
  'visit.image.zoomOut': 'Zoom out',
  'visit.image.fit': 'Reset view',
  'visit.image.brightness': 'Brightness',
  'visit.image.contrast': 'Contrast',
  'visit.image.read': 'Read image',
  'visit.image.reading': 'Reading…',
  'visit.image.readStatus.no_findings': 'The assistant found nothing reportable in this image. You were not charged.',
  'visit.image.readStatus.no_image': 'The image could not be read.',
  'visit.image.comment': 'Note on this mark',
  'visit.image.commentPlaceholder': 'What did you see here?',
  'visit.image.removeMark': 'Delete this mark',
  'visit.image.tooManyMarks': 'An image can hold at most {{max}} marks.',
  'visit.image.saveFailed': 'The marks could not be saved.',
  'visit.image.unsaved': 'Unsaved marks — press Save.',
  'visit.image.findings': 'What the assistant read',
  'visit.image.findingsNote': 'Suggestions. Nothing is charted until you confirm it.',
  'visit.image.confidence.high': 'Clear',
  'visit.image.confidence.medium': 'Probable',
  'visit.image.confidence.low': 'Uncertain',
  'visit.image.notLocalized': 'location not identified',
  'visit.image.unlocalizedNote': '{{count}} finding(s) could not be placed on the image and are listed only.',
  'visit.image.confirm': 'Confirm',

  'visit.files.openFile': 'Open file',
  'visit.image.register': 'Register',
  'visit.image.registering': 'Registering…',
  'visit.image.notRegistered.title': 'Not registered as a dental image',
  'visit.image.notRegistered.body': 'You can view it. Marking it up and having the assistant read it need a view type — a bitewing and a photograph do not show the same things.',
  'visit.image.changeView': 'Change view',
  'visit.image.viewType.title': 'View type',
  'visit.image.viewType.body': 'This decides what the assistant may report — a bitewing does not show a periapical lesion, and a photograph shows no bone. Correct it if it is wrong.',
  'visit.image.discard.title': 'Discard the marks you drew?',
  'visit.image.discard.body': 'They have not been saved and there is no other copy of them.',
  'visit.image.discard.confirm': 'Discard',
  'visit.files.error.title': 'Files could not be loaded',
  'visit.files.error.body': 'We could not read this patient’s documents. Please try again.',
  'visit.files.empty.title': 'No documents',
  'visit.files.empty.description': 'Upload a radiograph, a lab result or a referral letter.',
  'visit.files.upload': 'Upload document',
  'visit.files.uploading': 'Uploading…',
  'visit.files.uploadFailed': 'The document could not be uploaded.',
  'visit.files.readFailed': 'The file could not be read.',
  'visit.files.tooLarge': 'The file is larger than 15 MB.',
  'visit.files.docType': 'Document type',
  'visit.files.docTypes.lab_report': 'Lab report',
  'visit.files.docTypes.prescription': 'Prescription',
  'visit.files.docTypes.general': 'Other',
  'visit.files.attachTooth': 'Attach to tooth {{fdi}}',
  'visit.files.thisVisit': 'This visit',
  // The pairing §3.2.5 asks for: the report lives inside its own file's card.
  'visit.files.hasReport': 'Report',
  'visit.files.noReport': 'No report was extracted from this document.',
  'visit.files.previewFailed': 'The file could not be opened.',
  'visit.files.bytesGone': 'The original file is no longer stored; only its report remains.',
  'visit.files.remove': 'Delete {{name}}',
  'visit.files.units.b': 'B',
  'visit.files.units.kb': 'KB',
  'visit.files.units.mb': 'MB',

  // ── credits (اعتبار) ──────────────────────────────────────────────────────
  // The unit is «پارسه» / "Parseh" in BOTH locales — never «سکه», never "coin",
  // never the Latin "PC". Toman appears only where the number is a price.
  'credits.title': 'Parsina credit',
  'credits.subtitle': 'Your Parseh balance, what it was spent on, and how to add more.',
  'credits.unit': 'Parseh',
  'credits.toman': 'toman',
  'credits.tomanShort': 'T',
  'credits.currentBalance': 'Current balance',
  'credits.buy': 'Buy Parseh',
  'credits.topUp': 'Top up',
  'credits.percent': '{{value}}%',
  'credits.timesUsed': '{{count}}×',
  'credits.itemCount': '{{count}} entries',
  'credits.minutesUsed': '{{count}} min',
  'credits.byService': 'Where it went',
  'credits.transactions': 'Transactions',

  // Balance levels. `*Short` is the level on its own, for a chip too narrow for
  // the full phrase — the phone header uses it.
  'credits.level.low': 'Low balance',
  'credits.level.lowShort': 'Low',
  'credits.level.mid': 'Getting low',
  'credits.level.midShort': 'Fair',
  'credits.level.ok': 'Healthy balance',
  'credits.level.okShort': 'Good',
  'credits.banner.low': 'Only {{coins}} Parseh left. The assistant stops when it runs out.',
  'credits.banner.mid': '{{coins}} Parseh left — enough for a few more sessions.',

  // Service families, as the wallet knows them. The finer name of an action
  // comes from the transaction itself; these are the family it belongs to.
  'credits.service.assistant': 'Clinical assistant',
  'credits.service.interaction': 'Interaction check',
  'credits.service.transcription': 'Transcription',
  'credits.service.topUp': 'Top-up',
  'credits.service.gift': 'Gift code',
  'credits.service.sms': 'SMS',
  'credits.service.ai': 'AI service',

  'credits.filter.label': 'Filter transactions',
  'credits.filter.all': 'All',
  'credits.filter.spend': 'Spent',
  'credits.filter.topUp': 'Added',
  'credits.group.label': 'Group by',
  'credits.group.day': 'Day',
  'credits.group.type': 'Service',
  'credits.range.label': 'Date range',
  'credits.range.all': 'All time',
  'credits.range.days7': 'Last 7 days',
  'credits.range.days30': 'Last 30 days',
  'credits.range.thisMonth': 'This month',
  'credits.time.today': 'Today',
  'credits.time.yesterday': 'Yesterday',

  'credits.kpi.month': 'Spent this month',
  'credits.kpi.perVisit': 'Per session',
  'credits.kpi.topService': 'Biggest consumer',

  'credits.showingRange': 'Showing {{from}}–{{to}} of {{total}}',
  'credits.pager.page': 'Page {{page}} of {{total}}',
  'credits.pager.previous': 'Previous',
  'credits.pager.next': 'Next',

  'credits.empty.title': 'No transactions yet',
  'credits.empty.hint': 'Every assistant action in a session appears here.',
  'credits.empty.filtered': 'Nothing in this range',
  'credits.empty.filteredHint': 'Try a wider date range or a different filter.',
  'credits.error.title': 'Transactions unavailable',
  'credits.error.body': 'Your balance could not be read. Nothing has been charged.',

  // The price list. One row per action a dentist can actually take in this app —
  // there is no transcription row, because this app does not transcribe.
  'credits.priceList.title': 'What each action costs',
  'credits.priceList.hint': 'Charged only when you press the button — nothing runs on its own.',
  'credits.priceList.viewAll': 'Show all',
  // The list's only metered row. The service rounds up, so even the shortest
  // dictation is charged as a full minute.
  'credits.price.transcription': 'Voice dictation (speech to text)',
  'credits.price.differential': 'AI diagnosis suggestion',
  'credits.price.advice': 'Assistant advice',
  'credits.price.soap': 'Complete the note',
  'credits.price.teeth': 'Tooth findings from the narrative',
  'credits.price.confirmq': 'Confirmation questions',
  'credits.price.ask': 'Ask the assistant',
  'credits.price.ocr': 'Read an uploaded document',
  'credits.price.interaction': 'Drug interaction check',

  // Top-up
  'credits.topUp.chooseAmount': 'Choose an amount',
  'credits.topUp.recommended': 'Recommended',
  'credits.topUp.approxVisits': 'About {{count}} sessions with the assistant',
  'credits.topUp.custom': 'Another amount',
  'credits.topUp.customAria': 'Amount',
  'credits.topUp.payVia': 'Pay {{amount}} toman via {{gateway}}',
  'credits.topUp.enterAmount': 'Enter an amount',
  'credits.topUp.redirecting': 'Going to the gateway…',
  'credits.topUp.redirectNote': 'You will be taken to the bank gateway and returned here afterwards.',
  'credits.topUp.minimum': 'The smallest purchase is {{amount}} toman.',
  'credits.topUp.failed': 'The payment could not be started. Please try again.',
  'credits.topUp.failedWithReason': 'The payment could not be started: {{reason}}',
  'credits.gateway.saderat': 'Saderat Bank',
  'credits.gateway.zarinpal': 'Zarinpal',
  'credits.payment.done': 'Payment successful — your balance has been topped up.',
  'credits.payment.failed': 'The payment did not go through. Nothing was charged.',

  // Gift codes
  'credits.gift.title': 'Gift code',
  'credits.gift.hint': 'Have a code from a Parsina campaign? Enter it here and the Parseh lands in your balance.',
  'credits.gift.placeholder': 'PARSINA-1404-XXXX',
  'credits.gift.submit': 'Redeem',
  'credits.gift.submitting': 'Redeeming…',
  'credits.gift.empty': 'Enter the code first.',
  'credits.gift.notFound': 'This code is not valid. Enter it exactly as you received it.',
  'credits.gift.oneUse': 'Each code can be used once.',
  'credits.gift.success': '{{parseh}} Parseh added',
  'credits.gift.successSub': 'About {{visits}} sessions with the assistant.',
  'credits.gift.newBalance': 'New balance',
  'credits.gift.myCodes': 'Your codes',
  'credits.gift.worth': '{{parseh}} Parseh — about {{visits}} sessions',
  'credits.gift.status.issued': 'Unused',
  'credits.gift.status.redeemed': 'Used',
  'credits.gift.status.revoked': 'Revoked',
  'credits.gift.status.expired': 'Expired',

  // Low-balance alert. NOT the red floor this screen paints below 50 Parseh —
  // that is a colour rule; this is what the wallet sends a message about.
  'credits.lowBalance.title': 'Low-balance alert',
  'credits.lowBalance.hint': 'We will let you know when your balance falls below this.',
  'credits.lowBalance.toggleAria': 'Enable low-balance alerts',
  'credits.lowBalance.thresholdAria': 'Alert threshold, in Parseh',
  'credits.lowBalance.active': 'You will be warned below {{coins}} Parseh.',
  'credits.lowBalance.off': 'Alerts are off.',
  'credits.lowBalance.invalid': 'Enter a valid threshold.',
  'credits.lowBalance.saved': 'Alert settings saved.',
  'credits.lowBalance.failed': 'The alert settings could not be saved.',
};
